/**
 * Perimenopause closed-beta console — client types, derivations and document content.
 *
 * Design source: docs/presentations/bioweave_peri_beta_console_1.html (cd21e1d6).
 * That file is a self-contained Artifacts page persisting through `window.storage`
 * with no localStorage path — outside the Artifacts runtime a refresh wipes it.
 * This module is the console port: state lives server-side under /admin/beta/*.
 *
 * NOT here, deliberately:
 *  - the 24 gates and 13 timeline dates. Server-seeded (see beta-console-preflight);
 *    re-typing them client-side would give us two copies of MA-owned prose.
 *  - cohort strata and the blinded clinical review. Already built — see
 *    lib/periReview.ts and the /advisor/peri-review* endpoints. The blind is
 *    enforced server-side; do not re-derive it here.
 */

// --- Preflight gates -------------------------------------------------------

export type GateGroup = 'clinical' | 'governance' | 'ops' | 'deferred';
export type GateStatus = 'not_started' | 'in_progress' | 'blocked' | 'done' | 'deferred';

export type BetaGate = {
  id: string;
  group: GateGroup;
  label: string;
  detail: string;
  /** Free text — 'Dev' | 'Wil' | 'MA' today, but owners are prose, not an enum. */
  owner: string;
  blocking: boolean;
  status: GateStatus;
  note: string;
  /** Who last moved this gate, and when. Null while it sits at its seeded
   *  default: a gate nobody has touched has had no claim made on it. */
  changed_by: string | null;
  changed_at: string | null;
};

export type BetaGatesResponse = {
  gates: BetaGate[];
  version?: number | string | null;
  /** The gate whose closure lifts the oestrogen-sensitive exclusion. Named in
   *  the seed rather than hardcoded, and validated to exist server-side —
   *  a dangling id would have read as nobody being excluded. */
  exclusion_gate_id?: string | null;
};

/** A gate transition is a claim someone made. Unclaimed gates read as such
 *  rather than borrowing the seeded default's authority. */
export function gateAttribution(gate: BetaGate): string | null {
  if (!gate.changed_by) return null;
  return gate.changed_at ? `${gate.changed_by} · ${gate.changed_at.slice(0, 16).replace('T', ' ')}` : gate.changed_by;
}

export const GATE_GROUP_LABEL: Record<GateGroup, string> = {
  clinical: 'Clinical and safety',
  governance: 'Governance and consent',
  ops: 'Operations',
  deferred: 'Explicitly deferred — not blocking',
};

export const GATE_STATUS_META: Record<GateStatus, { label: string; cls: string }> = {
  not_started: { label: 'not started', cls: 'mod-code' },
  in_progress: { label: 'in progress', cls: 'mod-form' },
  blocked: { label: 'blocked', cls: 'mod-json' },
  done: { label: 'done', cls: 'mod-sign' },
  deferred: { label: 'deferred', cls: 'mod-calc' },
};

/** Gates that must clear before anyone is onboarded. The preflight banner is a
 *  count of these — bad state fails loud rather than degrading quietly. */
export function blockingOpen(gates: BetaGate[]): BetaGate[] {
  return gates.filter((g) => g.blocking && g.status !== 'done');
}

export function gatesByGroup(gates: BetaGate[]): Array<{ group: GateGroup; rows: BetaGate[] }> {
  const order: GateGroup[] = ['clinical', 'governance', 'ops', 'deferred'];
  return order
    .map((group) => ({ group, rows: gates.filter((g) => g.group === group) }))
    .filter((g) => g.rows.length > 0);
}

// --- Timeline --------------------------------------------------------------

export type BetaDate = {
  id: string;
  label: string;
  /** ISO yyyy-mm-dd. The only user-editable field — labels and notes are seeded. */
  date: string;
  /** The seeded date, so a milestone an operator has moved is visibly moved
   *  rather than silently re-baselined. */
  default_date?: string;
  /** A date that should not move without a reason you would write down. */
  gate: boolean;
  note: string;
};

/** True when an operator has moved this milestone off its seeded date. */
export function hasMoved(d: BetaDate): boolean {
  return !!d.default_date && d.default_date !== d.date;
}

/** Whole days from today to `date`; negative is past. Null when unset. */
export function daysUntil(date: string | undefined, now = new Date()): number | null {
  if (!date) return null;
  const today = new Date(now.toISOString().slice(0, 10) + 'T00:00:00');
  const target = new Date(date + 'T00:00:00');
  return Math.round((target.getTime() - today.getTime()) / 864e5);
}

export function relativeDayLabel(days: number | null): string {
  if (days === null) return '—';
  if (days === 0) return 'today';
  return days < 0 ? `${-days}d ago` : `in ${days}d`;
}

// --- Findings --------------------------------------------------------------

export type FindingCategory =
  | 'Safety' | 'Clinical validity' | 'Usefulness'
  | 'Trust calibration' | 'Reasoning stability' | 'UX' | 'Ops';
export type FindingSeverity = 'P0' | 'P1' | 'P2' | 'P3';
export type FindingSource =
  | 'Clinical review' | 'Onboarding interview' | 'Check-in'
  | 'Exit interview' | 'Telemetry' | 'Observation';
export type FindingStatus = 'Open' | 'Triaged' | 'In progress' | 'Fixed' | "Won't fix";

export const FINDING_CATEGORIES: FindingCategory[] = [
  'Safety', 'Clinical validity', 'Usefulness', 'Trust calibration', 'Reasoning stability', 'UX', 'Ops',
];
export const FINDING_SEVERITIES: FindingSeverity[] = ['P0', 'P1', 'P2', 'P3'];
export const FINDING_SOURCES: FindingSource[] = [
  'Clinical review', 'Onboarding interview', 'Check-in', 'Exit interview', 'Telemetry', 'Observation',
];
export const FINDING_STATUSES: FindingStatus[] = ['Open', 'Triaged', 'In progress', 'Fixed', "Won't fix"];

export type BetaFinding = {
  id: string;
  date: string;
  cat: FindingCategory;
  severity: FindingSeverity;
  src: FindingSource;
  participant_ref: string | null;
  desc: string;
  status: FindingStatus;
};

/** Closed states — a finding leaves the open count only when actioned or declined. */
const CLOSED: FindingStatus[] = ['Fixed', "Won't fix"];

export function isOpen(f: BetaFinding): boolean {
  return !CLOSED.includes(f.status);
}

export function openFindings(findings: BetaFinding[]): BetaFinding[] {
  return findings.filter(isOpen);
}

/** Open P0s. Any unsafe or contraindicated recommendation is a P0 regardless of
 *  anything else, so this count has halt authority on the protocol. */
export function openP0(findings: BetaFinding[]): BetaFinding[] {
  return openFindings(findings).filter((f) => f.severity === 'P0');
}

/** Severity first (P0 → P3), then most recent within a severity. */
export function sortFindings(findings: BetaFinding[]): BetaFinding[] {
  return [...findings].sort(
    (a, b) => a.severity.localeCompare(b.severity) || b.date.localeCompare(a.date),
  );
}

export const SEVERITY_CLS: Record<FindingSeverity, string> = {
  P0: 'mod-json', P1: 'mod-form', P2: 'mod-code', P3: 'mod-calc',
};

// --- Participant beta fields ----------------------------------------------

/** The activation gate a participant's latest run resolved to. `issued` is the
 *  only state in which they see a Plan rather than an interim report. */
export type ParticipantGateStatus =
  | '' | 'issued' | 'held_pre_baseline' | 'held_insufficient_evidence'
  | 'held_critical_drift' | 'board_abort';

export const PARTICIPANT_GATE_STATUSES: ParticipantGateStatus[] = [
  '', 'issued', 'held_pre_baseline', 'held_insufficient_evidence', 'held_critical_drift', 'board_abort',
];

/** Scheduling and run-observation fields the tester/cohort store does not carry.
 *  Joined onto the tester list by user_ref — pseudonym, stage, sub_branch and
 *  blinded live in the cohort store and are NOT duplicated here. */
export type BetaParticipant = {
  participant_ref: string;
  /** Baseline and T3 pathology draws. */
  draw1: string; draw3: string;
  /** Onboarding interview, then check-ins 1–3. */
  iv0: string; iv1: string; iv2: string; iv3: string;
  gate_status: ParticipantGateStatus;
  /** 0–100, recorded at every run — the bimodal-confidence residual is only
   *  visible if this is captured per run rather than once. */
  overall_confidence: number | null;
  /** ≥0.80 activates the Plan. */
  panel_coverage: number | null;
  notes: string;
};

// --- Cohort strata targets -------------------------------------------------

/** Purposive sampling targets from the protocol. Coverage of the design space,
 *  not representativeness — see the scope caveats. Cohort size and blinded split
 *  come from the live cohort_status (lib/periReview.ts); these are the strata the
 *  peri cohort store does not itself target. */
export type StratumTarget = { key: string; label: string; target: number; rationale: string };

export const STRATA_TARGETS: StratumTarget[] = [
  { key: 'dna', label: 'DNA export held', target: 3,
    rationale: 'Below three, the pharmacogenomic Safety Index goes effectively untested. Screen for it — do not order kits.' },
  { key: 'wearable', label: 'Wearable connected', target: 4,
    rationale: 'Enough to exercise the sync path without blocking the beta on the Capacitor bridge.' },
  { key: 'meds', label: 'Medications on board', target: 4,
    rationale: 'Drug–nutrient and drug–botanical interaction checking is a headline safety claim. A cohort on no medications does not test it.' },
];

// --- The document ----------------------------------------------------------

export type ProtocolRow = { term: string; body: string };

// Dates are NOT restated here — they live on the Timeline tab, are operator-
// editable, and a second copy in prose goes stale the first time one moves.
// These rows carry the reasoning; the Timeline carries the date.
export const LOCKED_DECISIONS: ProtocolRow[] = [
  { term: 'Build freeze', body: 'Hard. The date exists to stop the polishing, and it is the only thing here that is genuinely yours to hit. See Timeline.' },
  { term: 'Onboarding', body: 'Logistics-bound, not build-bound. Recruitment, consent, fasting draws and assay turnaround do not compress. See Timeline.' },
  { term: 'Cohort', body: '12, purposive across STRAW stage and sub_branch. Powered for insight saturation and review depth, not statistics.' },
  { term: 'Blinded review', body: '50% — 6 of 12. Reviewer records an independent impression from raw inputs before seeing any board output, then reveals and scores.' },
  { term: 'T3 re-draw', body: 'In. Drawn at week 11 so results are back and the board re-run before the exit interview.' },
  { term: 'DNA', body: 'Opt-in, existing AncestryDNA exports only. Kits take 6–8 weeks and will not return in time.' },
];

export type Objective = { n: number; name: string; halt?: boolean; body: string };

export const OBJECTIVES: Objective[] = [
  { n: 1, name: 'Safety', halt: true,
    body: 'Any unsafe or contraindicated recommendation is a P0 finding regardless of anything else. On real data specifically: does contraindication surfacing hold when inputs are messy, and did the exclusion actually filter? A safety failure halts the pathway.' },
  { n: 2, name: 'Clinical validity',
    body: 'Concordance with independent clinical judgment. Grounding soundness. Staging correctness. Whether the known bimodal-confidence residual manifests visibly.' },
  { n: 3, name: 'Usefulness',
    body: 'Three constructs, kept apart because they are trivially conflated: perceived helpfulness (self-report, weakest), behavioural engagement (did they act — observable, medium), clinical benefit (out of scope). This beta can credibly prove the first two.' },
  { n: 4, name: 'Trust calibration',
    body: 'Attribution-not-diagnosis is a core principle. Do users actually understand they are getting attribution, not diagnosis? Over-reliance, or substituting this for clinical care, is a safety-adjacent finding, not a UX nicety.' },
  { n: 5, name: 'Reasoning stability',
    body: 'Do real profiles trigger failure modes the synthetic set never produced? Board aborts, held gates, plan thrash between runs.' },
];

export const SYSTEM_BEHAVIOURS: ProtocolRow[] = [
  { term: 'Confidence gate × bimodal residual',
    body: 'The confidence gate prunes the plan below 60: at 40–59 only lab-directed, Tier-1 evidence and hard-safety compounds survive; below 40, lab-directed only. The accepted 12.5% bimodal confidence drop rate is therefore not a cosmetic residual here — roughly one participant in eight could receive a materially thinner plan for reasons unrelated to their health. Record overall_confidence at every run.' },
  { term: 'held_pre_baseline',
    body: 'The plan is held until activation resolves at ≥0.8 required-panel coverage. If the pathology panel does not satisfy perimenopause’s required panels, every participant reads "interim report" and never sees a Plan. This is the gate most likely to silently ruin the beta.' },
  { term: 'held_insufficient_evidence',
    body: 'Grounding confidence below the 80% threshold holds the formulation. On real, messier profiles the exclusion rate may exceed synthetic expectations. Record the gate status at every run.' },
  { term: 'Board abort',
    body: 'A specialist exhausting its retry budget aborts the run — no findings, no plan, a diagnostic to data/board_abort_diagnostics/ and a support notification. For a real participant on their first convene this is a catastrophic first experience. Aborts are a P1 finding by default.' },
  { term: 'Convene is user-initiated',
    body: 'Auto-trigger is off by default; the board runs only when the participant walks the picker → librarian → manifest → approve → convene flow. Engagement is not incidental — if they never convene, there is nothing to evaluate. Walk them through the first one.' },
  { term: 'clinical_test_request',
    body: 'The board may ask for tests. Whether it asks for the right ones, on real data, is free validation signal. Log every request and put it in front of a reviewer.' },
  { term: 'Safety Index needs genotype',
    body: 'The pharmacogenomic Safety Index cross-references SNP genotypes. Participants without a DNA export never trigger it — that safety layer goes untested unless the DNA stratum is filled. Note also the known silent-fail gaps (FUT2, GSTT1/GSTM1, CBS, SOD2) already flagged for Medical Advisory scope.' },
];

export const HALT_CRITERIA: string[] = [
  'Any reviewer records Safety = Fail → halt that pathway immediately. If the recommendation was acted on, contact the participant. Medical Advisory reviews before resume.',
  'Any contraindicated recommendation reaches a participant → halt the whole cohort pending review. No exceptions, no severity triage.',
  'A self-harm or crisis disclosure in chat (the companion\'s safety net) → escalation pathway within 24 hours, out of band from the beta entirely. Named clinician, not you.',
  'Board abort rate exceeds 20% → halt onboarding.',
  'Two or more participants show the same class of clinical-validity failure → halt onboarding, fix, re-run affected reports.',
];

export const CAN_ESTABLISH: string[] = [
  'Whether the reasoning is clinically sound and safe on real data, under independent review',
  'Whether the pipeline holds end to end, including longitudinally',
  'Whether users comprehend attribution-not-diagnosis',
  'Whether users engage and act — observable behaviour, not self-report',
];

export const CANNOT_ESTABLISH: ProtocolRow[] = [
  { term: 'Clinical benefit or efficacy',
    body: 'No control arm, n=12, self-selected, unblinded participants, three months. Perimenopausal symptom trajectories are noisy and non-linear; most lifestyle and supplement effects need 3–6 months to show in markers.' },
  { term: 'Generalisability',
    body: 'Purposive sampling is for coverage of the design space, not representativeness.' },
  { term: 'Ground truth',
    body: 'Reviewer judgment is expert opinion, not truth. Concordance measures agreement with a defensible standard, which is the honest thing it measures.' },
];

// --- Interview guides ------------------------------------------------------

export type GuideQuestion = { q: string; note?: string };
export type GuideSection = { heading: string; questions: GuideQuestion[] };
export type InterviewGuide = { id: string; when: string; title: string; sections: GuideSection[]; footer?: string };

export const INTERVIEW_GUIDES: InterviewGuide[] = [
  {
    id: 'onboarding',
    when: 'Within 72 hours of first reading the report · ~45 min',
    title: 'Onboarding interview',
    sections: [
      { heading: 'Comprehension — verbatim', questions: [
        { q: 'In your own words, what did the report tell you?' },
        { q: 'Is this a diagnosis?', note: 'the whole attribution-not-diagnosis principle lives or dies on the answer. Ask it flatly. Do not lead.' },
        { q: 'Who do you think worked out what is in here?' },
        { q: 'Where did the confidence number come from, do you think?' },
      ] },
      { heading: 'Their picture, before yours', questions: [
        { q: 'Describe what has been going on with your health over the past year or two.' },
        { q: 'What did you expect this to say? Did it say it?' },
        { q: 'Was there anything in here you already knew? Anything that surprised you?' },
        { q: 'Was there anything you expected and did not get?' },
      ] },
      { heading: 'Trust and safety', questions: [
        { q: 'What in here would you doubt? What would make you doubt the rest of it?' },
        { q: 'Did anything feel wrong, or unsafe, or like it did not know something about you that it should have?' },
        { q: 'What would you do next because of this?' },
        { q: 'Would you show this to a doctor? What do you think they would say?' },
      ] },
      { heading: 'The bar', questions: [
        { q: 'Three months from now, what would have to have happened for this to have been worth your time?' },
      ] },
    ],
  },
  {
    id: 'checkin',
    when: 'Monthly · ~30 min',
    title: 'Check-in',
    sections: [
      { heading: 'Behaviour, not opinion', questions: [
        { q: 'Walk me through the last time you opened it. What were you doing?', note: 'not "do you use it"' },
        { q: 'Did you run the board again?', note: 'convene is user-initiated; if they never convene there is nothing to evaluate. If they did not, find out why, precisely.' },
        { q: 'What have you actually done differently? What did you start, what did you stop?' },
        { q: 'What did you decide not to do, and why?' },
      ] },
      { heading: 'External validation — the free signal', questions: [
        { q: 'Have you taken any of this to a clinician? What did they say?', note: 'a GP disagreeing with the board is worth more than any self-report on this page. Capture it verbatim and route it to the review.' },
      ] },
      { heading: 'Friction and trust', questions: [
        { q: 'What confused you?' },
        { q: 'What did you not believe?' },
        { q: 'Did anything feel unsafe or wrong?' },
        { q: 'If the report changed since last time — did the change make sense to you?', note: 'coherent evolution vs thrash, from the user’s side' },
      ] },
    ],
  },
  {
    id: 'exit',
    when: 'After the M3 report · ~60 min',
    title: 'Exit interview',
    sections: [
      { heading: 'Comprehension, re-tested', questions: [
        { q: 'In your own words, what does this tell you?', note: 'identical wording to onboarding. Comprehension drift over three months is a finding.' },
        { q: 'Is this a diagnosis?' },
      ] },
      { heading: 'The honest questions', questions: [
        { q: 'Would you keep using this?', note: 'then wait. Do not fill the silence.' },
        { q: 'Would you pay for it? What would you pay? What would you expect for that?' },
        { q: 'What would you tell someone in your situation about it?' },
        { q: 'What is the single thing that would have made this materially more useful?' },
        { q: 'What did we get wrong about you?' },
      ] },
      { heading: 'Longitudinal', questions: [
        { q: 'Looking at the first report and this one together — does the change reflect what actually happened to you?' },
      ] },
    ],
    footer: 'Discount "would you pay" answers heavily. Participants who received free bloods from a founder they have met are not a pricing signal. Behavioural engagement across the three check-ins is the honest read, and it is the one to carry forward.',
  },
];

// --- Markdown export -------------------------------------------------------

/** Findings log + disagreement catalogue as one document.
 *
 *  The catalogue rows are NOT re-derived here — they come from the peri-review
 *  queue, where the server already computed them from signed verdicts. This
 *  function only merges. */
export function findingsMarkdown(input: {
  findings: BetaFinding[];
  catalogue: Array<{ participant_ref: string; category: string; value: string; note: string }>;
  concordance: { scored: number; passing: number; pct: number | null };
  blockingGates: number;
  today: string;
}): string {
  const { findings, catalogue, concordance, blockingGates, today } = input;
  const L: string[] = ['# Perimenopause beta — findings', '', `Exported ${today}`, ''];

  L.push(
    `Primary metric: ${concordance.scored ? `${concordance.passing}/${concordance.scored}` : '—'}` +
    ' (no safety issue + substantive concordance at baseline). Threshold ≥80%, zero safety failures.',
    '',
    `Blocking gates open: ${blockingGates}. Open findings: ${openFindings(findings).length} (${openP0(findings).length} P0).`,
    '',
  );

  for (const sev of FINDING_SEVERITIES) {
    const rows = findings.filter((f) => f.severity === sev);
    if (!rows.length) continue;
    L.push(`## ${sev}`, '');
    for (const f of rows) {
      L.push(
        `- **${f.cat}** · ${f.status} · ${f.src}${f.participant_ref ? ` · ${f.participant_ref}` : ''} · ${f.date}`,
        `  ${f.desc}`,
      );
    }
    L.push('');
  }

  if (catalogue.length) {
    L.push('## Disagreement catalogue', '');
    const byParticipant = new Map<string, typeof catalogue>();
    for (const row of catalogue) {
      const list = byParticipant.get(row.participant_ref) ?? [];
      list.push(row);
      byParticipant.set(row.participant_ref, list);
    }
    for (const [ref, rows] of byParticipant) {
      L.push(`### ${ref}`);
      for (const r of rows) L.push(`- **${r.category}** → ${r.value}${r.note ? ` — ${r.note}` : ''}`);
      L.push('');
    }
  }

  return L.join('\n');
}
